/* IMPORTANT: Before modifying this file, please update CHANGELOG.md with a summary of your changes.
Also, make clear comments about every change in this file and what it was replacing so that we don't end up trying the same fixes repeatedly. */

// API base URL - Use relative URL by default to avoid Content Security Policy issues in production
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api' : '/api');

interface ApiRequestData {
  apiName: string;
  useCase: string;
  name: string;
  email: string;
}

interface ApiResponse {
  markdown: string;
}

interface IntegrationRequestResponse {
  status: string;
  message: string;
  data: {
    requestId: string;
    status: string;
  };
}

interface IntegrationStatusResponse {
  status: string;
  data: {
    requestId: string;
    apiName: string;
    status: string;
    result?: string;
    errorMessage?: string;
    createdAt: string;
    updatedAt: string;
  };
}

/**
 * Sends a request to the API to generate integration instructions
 * 
 * @param data The form data to send to the API
 * @returns A promise that resolves to the API response
 */
export const generateIntegrationPrompt = async (data: ApiRequestData): Promise<ApiResponse> => {
  try {
    // Step 1: Create the integration request
    const createResponse = await fetch(`${API_BASE_URL}/integration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!createResponse.ok) {
      const errorData = await createResponse.json();
      throw new Error(errorData.message || 'Failed to create integration request');
    }
    
    const createResult: IntegrationRequestResponse = await createResponse.json();
    const requestId = createResult.data.requestId;
    
    // Step 2: Poll for the result
    const result = await pollIntegrationStatus(requestId);
    
    return {
      markdown: result.data.result || ''
    };
  } catch (error) {
    console.error('Error generating integration prompt:', error);
    
    // Fallback to mock data if the API is not available
    return fallbackGenerateIntegrationPrompt(data);
  }
};

/**
 * Polls the API for the status of an integration request
 * 
 * @param requestId The ID of the integration request
 * @param maxAttempts Maximum number of polling attempts
 * @param interval Polling interval in milliseconds
 * @returns A promise that resolves to the integration status response
 */
const pollIntegrationStatus = async (
  requestId: string,
  maxAttempts: number = 30,
  interval: number = 2000
): Promise<IntegrationStatusResponse> => {
  let attempts = 0;
  
  const poll = async (): Promise<IntegrationStatusResponse> => {
    attempts++;
    
    const response = await fetch(`${API_BASE_URL}/integration/${requestId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get integration status: ${response.statusText}`);
    }
    
    const result: IntegrationStatusResponse = await response.json();
    
    if (result.data.status === 'completed') {
      return result;
    } else if (result.data.status === 'failed') {
      throw new Error(result.data.errorMessage || 'Integration request failed');
    } else if (attempts >= maxAttempts) {
      throw new Error('Maximum polling attempts reached');
    }
    
    // Wait for the specified interval
    await new Promise(resolve => setTimeout(resolve, interval));
    
    // Try again
    return poll();
  };
  
  return poll();
};

/**
 * Fallback function to generate integration prompt when the API is not available
 * 
 * @param data The form data
 * @returns A mock API response
 */
const fallbackGenerateIntegrationPrompt = (data: ApiRequestData): ApiResponse => {
  console.warn('Using fallback integration prompt generation');
  
  return {
    markdown: `# ${data.apiName} Integration Guide

## Overview
This guide will help you integrate with ${data.apiName} based on your use case: ${data.useCase}.

## Prerequisites
- An account with ${data.apiName}
- API credentials (client ID and secret)
- Basic understanding of RESTful APIs

## Step 1: Register Your Application
1. Log in to the ${data.apiName} developer portal
2. Create a new application
3. Note your client ID and secret

## Step 2: Authentication
\`\`\`javascript
const authenticate = async () => {
  const response = await fetch('https://api.example.com/oauth/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      client_id: 'YOUR_CLIENT_ID',
      client_secret: 'YOUR_CLIENT_SECRET',
      grant_type: 'client_credentials'
    })
  });
  
  const data = await response.json();
  return data.access_token;
};
\`\`\`

## Step 3: Make API Calls
\`\`\`javascript
const makeApiCall = async (endpoint, accessToken) => {
  const response = await fetch(\`https://api.example.com/\${endpoint}\`, {
    headers: {
      'Authorization': \`Bearer \${accessToken}\`
    }
  });
  
  return await response.json();
};
\`\`\`

## Step 4: Implement Webhooks (Optional)
For real-time updates, you can implement webhooks:

1. Register a webhook URL in the developer portal
2. Create an endpoint to receive webhook events
3. Process the events as needed

## Additional Resources
- [API Documentation](https://example.com/docs)
- [Developer Forum](https://example.com/forum)
- [Sample Code Repository](https://github.com/example/api-samples)

## Support
If you need assistance, contact ${data.apiName} support or email us at support@example.com.

---
Generated for ${data.name} (${data.email}) on ${new Date().toLocaleDateString()}
`
  };
};